import { PrimaryButton } from '@/common/Button';
import TrackWrapper from '@/common/TrackWrapper';
import { defaultImageHandler } from '@/home/utils/defaultImageHandler';
import formatEvent from '@/home/utils/formatEvent';
import OrderState from '~/constants/home/orderState';
import { InventoryTypeKey } from '~/constants/inventoryType';
import { useSourcedHandleNativePurchaseOnClick } from '~/native/common/hooks';
import {
  getNativeVdpPayload,
  nativeMyCarsPayload
} from '~/native/common/utils';
import {
  BlueDot,
  buttonStyles,
  Card,
  cardWrapperStyles,
  DetailsContentWrapper,
  DetailsWrapper,
  Image,
  ImageContainer,
  SubTitle,
  Title,
  TitleWrapper,
  TrimMileage,
  YMM,
  YMMTMWrapper
} from './OrderStatusCard.styles';
import StatusCardType from './StatusCardType';
import {
  getCtaLink,
  getCtaText,
  getCustomAttributesDescription,
  getSubText,
  getTitleText
} from './utils';
import { RemainingTasks } from './VerificationTasks';

type OwnProps = {
  cardStatus: StatusCardType;
  orderState: OrderState
  nativeSource: string;
  vehicle?: {
    vehicleId: number;
    stockNumber: number;
    inventoryType: InventoryTypeKey;
    make: string;
    model: string;
    trim: string;
    year: number;
    mileage: number;
    imageUrl: string;
  }
  openVerifications?: {
    hasOpenVerifications: boolean;
    openVerificationsCount: number;
  };
};

type ImageProps = {
  imageUrl: string;
  stockNumber: number;
  inventoryType: InventoryTypeKey;
};

const cvnaVexFastlyImageUrl = 'https://vexgateway.fastly.carvana.io';
const cvnaAppAssetsCdn = process.env.CVNA_APP_CDN_URL ?? '';

function getStatusImageToUse({
  imageUrl,
  stockNumber,
  inventoryType
}: ImageProps) {
  const hasStockNumber = Boolean(stockNumber);
  const isWip = inventoryType === InventoryTypeKey.WIP;

  if (!hasStockNumber && !imageUrl) {
    return `${cvnaAppAssetsCdn}/search-assets/noevox.png`;
  }

  return isWip
    ? imageUrl
    : `${cvnaVexFastlyImageUrl}/${stockNumber}/standardizedHero.jpg?width=688&crop=16/9`;
}

function VehicleImage({
  ctaLink,
  customAttributes,
  sendContinuePurchaseNativePayload,
  getNativePayload,
  handleNativePurchaseOnClick,
  imageUrl,
  inventoryType,
  make,
  model,
  nativePayload,
  stockNumber
}) {
  return (
    <ImageContainer>
      <a
        href={ctaLink}
        data-qa="Status Card Image CTA"
        data-analytics-id="Home feed card"
        data-analytics-attributes={JSON.stringify(customAttributes)}
        data-native-included={!sendContinuePurchaseNativePayload}
        data-native-payload={getNativePayload()}
        onClick={event => {
          if (sendContinuePurchaseNativePayload) {
            handleNativePurchaseOnClick(event, nativePayload);
          }
        }}
        className="order-2 flex w-full"
      >
        <Image
          alt={`${make} ${model}`}
          src={getStatusImageToUse({
            imageUrl,
            stockNumber,
            inventoryType
          })}
          onError={defaultImageHandler}
        />
      </a>
    </ImageContainer>
  );
}

export default function OrderStatusCard({
  cardStatus,
  orderState,
  nativeSource,
  vehicle,
  openVerifications
}: OwnProps) {
  const handleNativePurchaseOnClick = useSourcedHandleNativePurchaseOnClick(nativeSource);

  if (
    !vehicle ||
    (cardStatus === StatusCardType.OrderPlaced && !openVerifications)
  ) {
    return null;
  }

  const {
    make,
    model,
    imageUrl,
    vehicleId,
    trim,
    year,
    inventoryType,
    stockNumber,
    mileage = 0
  } = vehicle;

  const customAttributes = {
    description: getCustomAttributesDescription(cardStatus),
    vehicleId
  };

  const title = getTitleText(
    cardStatus,
    make,
    openVerifications?.hasOpenVerifications ?? false
  );
  const subText = getSubText(
    cardStatus,
    openVerifications?.hasOpenVerifications ?? false,
    orderState
  );
  const ctaText = getCtaText(cardStatus);
  const ctaLink = getCtaLink(cardStatus, vehicleId);

  const sendContinuePurchaseNativePayload =
    cardStatus === StatusCardType.PurchaseInProgress;

  const shouldShowRemainingTasks =
    cardStatus === StatusCardType.OrderPlaced &&
    (openVerifications?.hasOpenVerifications ?? false);

  function getNativePayload() {
    switch (cardStatus) {
      case StatusCardType.PurchaseInProgress:
        return '';
      case StatusCardType.OrderPlaced:
      case StatusCardType.Purchased:
        return nativeMyCarsPayload;
      case StatusCardType.Reserved:
        return getNativeVdpPayload(vehicleId);
    }
  }

  const nativePayload = {
    make,
    model,
    trim,
    year,
    vehicleId,
    vehicleImage: imageUrl,
    inventoryType,
    stockNumber
  };

  return (
    <TrackWrapper
      event={formatEvent('Viewed - Home feed card', customAttributes)}
      className={cardWrapperStyles}
    >
      <Card>
        <div className="flex flex-col justify-center gap-2xl sm:min-w-[316px] sm:p-xl md:min-w-[508px]">
          <TitleWrapper>
            <Title className=" text-blue-6 tl-header-m m:tl-header-xl">
              {title}
            </Title>
            <SubTitle className="text-grey-5 tl-body-m m:tl-body-l">
              {subText}
            </SubTitle>
          </TitleWrapper>
          {shouldShowRemainingTasks && (
            <a
              href={ctaLink}
              data-qa="Status Card Btn CTA"
              data-analytics-id="Home feed card"
              data-analytics-attributes={JSON.stringify(customAttributes)}
              data-native-included={!sendContinuePurchaseNativePayload}
              data-native-payload={getNativePayload()}
              onClick={event => {
                if (sendContinuePurchaseNativePayload) {
                  handleNativePurchaseOnClick(event, nativePayload);
                }
              }}
              className="block m:hidden"
            >
              <RemainingTasks
                cta={ctaText}
                hasOpenVerifications={
                  openVerifications?.hasOpenVerifications ?? false
                }
                openVerificationsCount={
                  openVerifications?.openVerificationsCount ?? 0
                }
              />
            </a>
          )}
          <DetailsWrapper>
            <DetailsContentWrapper>
              <YMMTMWrapper>
                <YMM>
                  {year} {make} {model}
                </YMM>
                <TrimMileage>
                  {trim}{' '}
                  {mileage ? (
                    <>
                      <BlueDot /> {mileage.toLocaleString()} miles
                    </>
                  ) : (
                    ''
                  )}
                </TrimMileage>
              </YMMTMWrapper>
              {shouldShowRemainingTasks ? (
                <a
                  href={ctaLink}
                  data-qa="order-placed-verifications-button"
                  data-analytics-id="Home feed card"
                  data-analytics-attributes={JSON.stringify(customAttributes)}
                  data-native-included={!sendContinuePurchaseNativePayload}
                  data-native-payload={getNativePayload()}
                  onClick={event => {
                    if (sendContinuePurchaseNativePayload) {
                      handleNativePurchaseOnClick(event, nativePayload);
                    }
                  }}
                  className="order-1 hidden s:order-2 m:block"
                >
                  <RemainingTasks
                    cta={ctaText}
                    hasOpenVerifications={
                      openVerifications?.hasOpenVerifications ?? false
                    }
                    openVerificationsCount={
                      openVerifications?.openVerificationsCount ?? 0
                    }
                  />
                </a>
              ) : (
                <a
                  href={ctaLink}
                  data-analytics-id="Home feed card"
                  data-qa="Status Card Btn CTA"
                  data-analytics-attributes={JSON.stringify(customAttributes)}
                  data-native-included={!sendContinuePurchaseNativePayload}
                  data-native-payload={getNativePayload()}
                  onClick={event => {
                    if (sendContinuePurchaseNativePayload) {
                      handleNativePurchaseOnClick(event, nativePayload);
                    }
                  }}
                  className="s:order-3"
                >
                  <PrimaryButton className={buttonStyles}>
                    {ctaText}
                  </PrimaryButton>
                </a>
              )}
              <div className="block m:hidden">
                <VehicleImage
                  ctaLink={ctaLink}
                  customAttributes={customAttributes}
                  sendContinuePurchaseNativePayload={
                    sendContinuePurchaseNativePayload
                  }
                  getNativePayload={getNativePayload}
                  handleNativePurchaseOnClick={handleNativePurchaseOnClick}
                  imageUrl={imageUrl}
                  inventoryType={inventoryType}
                  make={make}
                  model={model}
                  nativePayload={nativePayload}
                  stockNumber={stockNumber}
                />
              </div>
            </DetailsContentWrapper>
          </DetailsWrapper>
        </div>
        <div className="hidden flex-col m:flex">
          <VehicleImage
            ctaLink={ctaLink}
            customAttributes={customAttributes}
            sendContinuePurchaseNativePayload={
              sendContinuePurchaseNativePayload
            }
            getNativePayload={getNativePayload}
            handleNativePurchaseOnClick={handleNativePurchaseOnClick}
            imageUrl={imageUrl}
            inventoryType={inventoryType}
            make={make}
            model={model}
            nativePayload={nativePayload}
            stockNumber={stockNumber}
          />
        </div>
      </Card>
    </TrackWrapper>
  );
}
